/**
 * The `callbacks` slice of the SimpleTable context value.
 *
 * Split out of `SimpleTableContextValue.ts` (module-size lane L17). Behaviour is
 * pinned by `tests/universal/simple-table-context-value.test.ts`.
 *
 * This is an explicit ALLOW-LIST, not a spread: `useSimpleTableCallbacks` also
 * exposes factories (`createHandleSelectAll`, `createHandleBulkAction`) and
 * intermediate handlers (the two-argument scroll callbacks) that consumers must
 * NOT reach for. Keeping the list literal is what stops a new internal helper
 * leaking into the public context surface by accident.
 */

import type { BaseEntity } from "../types";
import type { SimpleTableContextValue } from "../contexts/SimpleTableContext";

/**
 * The composed callback set `SimpleTable` hands to the context builder.
 *
 * THIS TYPE IS THE GUARD. It used to be `Record<string, (...args: never[]) =>
 * unknown>` — an index signature, under which EVERY name destructured below
 * type-checks whether or not the producer supplies it. `useSimpleTableCallbacks`
 * returned five members while this file destructured twenty-seven, so eighteen
 * context callbacks were `undefined` at runtime (`TableRow` calls
 * `callbacks.handleSelectItem(...)` ungated) and `tsc` had nothing to say about
 * it for three months. Naming the members exactly — they ARE the context's own
 * published callback surface — makes a missing one a compile error at the call
 * site that builds the object. Do not widen this back to an index signature.
 */
export type ComposedTableCallbacks<TData extends BaseEntity = BaseEntity> =
  SimpleTableContextValue<TData>["callbacks"];

export function buildCallbacksSlice<TData extends BaseEntity>(
  callbacks: ComposedTableCallbacks<TData>
): SimpleTableContextValue<TData>["callbacks"] {
  const {
    handleColumnResizeCallback,
    resetColumnWidthsCallback,
    handleDragStartCallback,
    handleDragOverCallback,
    handleDropCallback,
    handleDragEndCallback,
    handleCellEditCallback,
    handleCellSaveCallback,
    handleCellCancelCallback,
    handleSort,
    handleSelectItem,
    handleBulkEditField,
    handleBulkEditAllFiltered,
    handleCustomRangeClick,
    handleCustomRangeApply,
    handleCustomRangeClose,
    removeSortColumn,
    clearAllSorting,
    handleSelectAll,
    handleBulkAction,
    handleBodyScroll,
    handleHeaderScroll,
    addAdvancedFilter,
    removeAdvancedFilter,
    clearAllAdvancedFilters,
  } = callbacks;

  return {
    handleSelectItem,
    handleSelectAll,
    handleCellEditCallback,
    handleCellSaveCallback,
    handleCellCancelCallback,
    handleSort,
    removeSortColumn,
    clearAllSorting,
    handleCustomRangeClick,
    handleCustomRangeApply,
    handleCustomRangeClose,
    resetColumnWidthsCallback,
    handleColumnResizeCallback,
    handleDragStartCallback,
    handleDragOverCallback,
    handleDropCallback,
    handleDragEndCallback,
    handleBulkEditField,
    handleBulkEditAllFiltered,
    handleBulkAction,
    handleBodyScroll,
    handleHeaderScroll,
    addAdvancedFilter,
    removeAdvancedFilter,
    clearAllAdvancedFilters,
  };
}
